// Utility functions for generating insights from chart data
console.log('[Insights] Module loaded successfully');

/**
 * Find the brand with the highest value for a given metric
 */
export function findLeader(data, metric) {
  if (!data || data.length === 0) return "";
  return data.reduce((max, item) =>
    (item[metric] > max[metric] ? item : max)
  );
}

/**
 * Find the second-place brand for a given metric
 */
export function findRunnerUp(data, metric, leaderBrand) {
  if (!data || data.length === 0) return "";
  const filtered = data.filter(item => item.brand !== leaderBrand);
  if (filtered.length === 0) return "";
  return filtered.reduce((max, item) =>
    (item[metric] > max[metric] ? item : max)
  );
}

/**
 * Calculate percentage difference
 */
export function calculatePercentageDiff(value1, value2) {
  if (value2 === 0) return 0;
  return ((value1 - value2) / value2 * 100).toFixed(1);
}

/**
 * Format large numbers with K/M suffix
 */
export function formatLargeNumber(num) {
  if (num === undefined || num === null || isNaN(num)) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}

/**
 * Extract brand names dynamically from data objects
 * @param {Array} data - Array of data objects
 * @param {string} identifyingProperty - The property name that identifies the category (e.g., 'tier', 'category', 'format', 'genre')
 * @returns {Array} - Array of unique brand names
 */
export function extractBrandsFromData(data, identifyingProperty) {
  if (!data || data.length === 0) return [];
  
  const brands = new Set();
  data.forEach(item => {
    if (item && typeof item === 'object') {
      Object.keys(item).forEach(key => {
        // Include all keys that are not the identifying property and not common data properties
        if (key !== identifyingProperty && 
            key !== 'brand' && 
            typeof item[key] === 'number') {
          brands.add(key);
        }
      });
    }
  });
  
  return Array.from(brands);
}

function formatViews(num) {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
}

/**
 * Generate insight for Share of Voice Donut chart
 */
// export function generateShareOfVoiceInsight(data) {
//   if (!data || data.length === 0) return "";

//   const leader = findLeader(data, 'value');
//   if (!leader) return "";

//   const runnerUp = findRunnerUp(data, 'value', leader.brand);

//   if (!runnerUp) {
//     return `${leader.brand} commands ${leader.value}% share of voice with ${formatViews(leader.views)} views.`;
//   }

//   const gap = (leader.value - runnerUp.value).toFixed(0);

//   return `${leader.brand} commands ${leader.value}% share of voice with ${formatViews(leader.views)} views, leading ${runnerUp.brand} by ${gap} percentage points (${runnerUp.value}%, ${formatViews(runnerUp.views)} views).`;
// }

export function generateShareOfVoiceInsight(data) {
  if (!data || data.length === 0) return "";

  // Sort brands by value (descending)
  const sortedData = [...data].sort((a, b) => b.value - a.value);

  const leader = sortedData[0];
  const restBrands = sortedData.slice(1);

  // If only one brand
  if (restBrands.length === 0) {
    return `${leader.brand} leads Share of Voice with ${leader.value}% of total category views (${formatViews(leader.views)}).`;
  }

  // Create "followed by ..." part
  const followedByText = restBrands
    .map(
      (b) =>
        `${b.brand} (${b.value}%, ${formatViews(b.views)})`
    )
    .join(", ");

  return `${leader.brand} leads Share of Voice with ${leader.value}% of total category views (${formatViews(leader.views)}), followed by ${followedByText}.`;
}

/**
 * Generate insight for Market Position Scatter chart
 */
export function generateMarketPositionInsight(data) {
  if (!data || data.length === 0) return "";

  const highestEngagement = findLeader(data, 'engagementRate');
  const mostInfluencers = findLeader(data, 'influencerCount');
  const highestVolume = findLeader(data, 'postVolume');

  if (highestEngagement.brand === mostInfluencers.brand) {
    return `${highestEngagement.brand} dominates with ${highestEngagement.influencerCount} influencers and highest engagement rate at ${highestEngagement.engagementRate}%, generating ${highestVolume.postVolume} posts.`;
  }

  return `${highestEngagement.brand} achieves highest engagement rate at ${highestEngagement.engagementRate}% with ${highestEngagement.influencerCount} influencers. ${mostInfluencers.brand} maximizes reach with ${mostInfluencers.influencerCount} influencers at ${mostInfluencers.engagementRate}% engagement.`;
}

/**
 * Generate insight for Tier Distribution chart
 */
export function generateTierDistributionInsight(data) {
  if (!data || data.length === 0) return "";

  // Find tier 1 (highest tier) data
  const tier1 = data.find(d => d.tier === 'Tier 1 (1M+)');
  if (!tier1) return "";

  const brands = extractBrandsFromData(data, 'tier');
  if (brands.length === 0) return "";

  const tier1Values = brands.map(brand => ({ brand, count: tier1[brand] || 0 }));
  const leader = tier1Values.reduce((max, item) => item.count > max.count ? item : max);
  const runnerUp = tier1Values.filter(b => b.brand !== leader.brand)
    .reduce((max, item) => item.count > max.count ? item : max);

  return `${leader.brand} dominates Tier 1 (1M+ followers) with ${leader.count} influencers vs ${runnerUp.brand}'s ${runnerUp.count}. ${leader.brand} maintains leadership in premium influencer partnerships.`;
}

/**
 * Generate insight for Category Breakdown chart
 */
export function generateCategoryBreakdownInsight(data) {
  if (!data || data.length === 0) return "";

  const brands = extractBrandsFromData(data, 'category');
  if (brands.length === 0) return "";

  const brandTotals = {};
  const brandTopCategories = {};

  brands.forEach(brand => {
    brandTotals[brand] = 0;
    brandTopCategories[brand] = { category: '', count: 0 };
  });

  // Calculate totals and find top category for each brand
  data.forEach(item => {
    brands.forEach(brand => {
      const count = item[brand] || 0;
      brandTotals[brand] += count;
      if (count > brandTopCategories[brand].count) {
        brandTopCategories[brand] = { category: item.category, count };
      }
    });
  });

  // Find overall leader
  const leaderBrand = Object.keys(brandTotals).reduce((a, b) =>
    brandTotals[a] > brandTotals[b] ? a : b
  );

  // Find most interesting diverse leaders
  const topCat = brandTopCategories[leaderBrand];
  const otherLeaders = brands.filter(b => b !== leaderBrand)
    .map(b => ({ brand: b, ...brandTopCategories[b] }))
    .sort((a, b) => b.count - a.count)[0];

  return `${leaderBrand} leads in ${topCat.category} (${topCat.count} posts). ${otherLeaders.brand} dominates ${otherLeaders.category} category (${otherLeaders.count} posts), suggesting distinct positioning strategies across categories.`;
}

/**
 * Generate insight for Share of Voice Detailed chart
 */
export function generateShareOfVoiceDetailedInsight(data) {
  if (!data || data.length === 0) return "";

  // Find brand with best engagement-to-post ratio
  const withRatio = data.map(item => ({
    ...item,
    ratio: (item.engagementShare / item.postsShare) || 0
  }));

  const bestEfficiency = withRatio.reduce((max, item) =>
    item.ratio > max.ratio ? item : max
  );

  const leader = findLeader(data, 'postsShare');

  if (bestEfficiency.brand === leader.brand) {
    return `${leader.brand} leads with ${leader.postsShare}% post share and achieves highest engagement efficiency with ${leader.engagementShare}% engagement share, demonstrating superior content quality.`;
  }

  return `${bestEfficiency.brand} overperforms with ${bestEfficiency.engagementShare}% engagement share vs ${bestEfficiency.postsShare}% post share (${(bestEfficiency.ratio * 100 - 100).toFixed(0)}% efficiency gain). ${leader.brand} leads in volume with ${leader.postsShare}% post share.`;
}

/**
 * Generate insight for Influencer Overlap chart
 */
export function generateInfluencerOverlapInsight(data) {
  if (!data || data.length === 0) return "";

  const withExclusivityRate = data.map(item => ({
    ...item,
    total: item.exclusive + item.shared,
    exclusivityRate: (item.exclusive / (item.exclusive + item.shared) * 100) || 0
  }));

  const bestExclusivity = withExclusivityRate.reduce((max, item) =>
    item.exclusivityRate > max.exclusivityRate ? item : max
  );

  const mostExclusive = withExclusivityRate.reduce((max, item) =>
    item.exclusive > max.exclusive ? item : max
  );

  if (bestExclusivity.brand === mostExclusive.brand) {
    return `${bestExclusivity.brand} maintains strongest exclusivity with ${bestExclusivity.exclusive} exclusive vs ${bestExclusivity.shared} shared influencers (${bestExclusivity.exclusivityRate.toFixed(0)}% exclusive rate), demonstrating strong brand partnerships.`;
  }

  return `${bestExclusivity.brand} leads in exclusivity rate at ${bestExclusivity.exclusivityRate.toFixed(0)}% (${bestExclusivity.exclusive}/${bestExclusivity.total}). ${mostExclusive.brand} has most exclusive partnerships with ${mostExclusive.exclusive} exclusive influencers (${mostExclusive.exclusivityRate.toFixed(0)}% rate).`;
}

/**
 * Generate insight for Risk Distribution chart
 */
export function generateRiskDistributionInsight(data) {
  if (!data || data.length === 0) return "";

  const withRiskScore = data.map(item => ({
    ...item,
    total: item.low + item.medium + item.high,
    lowPercent: (item.low / (item.low + item.medium + item.high) * 100) || 0,
    riskScore: (item.low * 1 + item.medium * 2 + item.high * 3) / (item.low + item.medium + item.high) // Lower is better
  }));

  const lowestRisk = withRiskScore.reduce((min, item) =>
    item.riskScore < min.riskScore ? item : min
  );

  return `${lowestRisk.brand} demonstrates best risk management with ${lowestRisk.low} low-risk, ${lowestRisk.medium} medium-risk, and only ${lowestRisk.high} high-risk influencers (${lowestRisk.lowPercent.toFixed(0)}% low-risk rate). This portfolio suggests stronger brand safety.`;
}

/**
 * Generate insight for Post Format Analysis chart
 */
export function generatePostFormatInsight(data) {
  if (!data || data.length === 0) return "";

  const brands = extractBrandsFromData(data, 'format');
  if (brands.length === 0) return "";

  // Find Reels format (typically highest engagement)
  const reelsData = data.find(d => d.format === 'Reels');
  if (!reelsData) return "";

  const reelsLeader = brands.reduce((leader, brand) =>
    (reelsData[brand] || 0) > (reelsData[leader] || 0) ? brand : leader
  );

  const reelsCount = reelsData[reelsLeader];
  const totalPosts = data.reduce((sum, format) => sum + (format[reelsLeader] || 0), 0);
  const reelsPercent = ((reelsCount / totalPosts) * 100).toFixed(0);

  return `${reelsLeader} leads in Reels production with ${reelsCount} posts (${reelsPercent}% of their content), capitalizing on the highest-engagement format. Reels continue to drive superior performance across all brands.`;
}

/**
 * Generate insight for Genre Distribution chart
 */
export function generateGenreDistributionInsight(data) {
  if (!data || data.length === 0) return "";

  const brands = extractBrandsFromData(data, 'genre');
  if (brands.length === 0) return "";

  // Find Mega tier (highest reach potential)
  const megaTier = data.find(d => d.genre === 'Mega (1M above)');
  if (!megaTier) return "";

  const megaLeader = brands.reduce((leader, brand) => {
    const currentValue = megaTier[brand] || 0;
    const leaderValue = megaTier[leader] || 0;
    return currentValue > leaderValue ? brand : leader;
  }, brands[0]);

  const megaCount = megaTier[megaLeader];

  // Find Micro tier (scalability)
  const microTier = data.find(d => d.genre === 'Micro (10K-100K)');
  if (!microTier) return `${megaLeader} achieves best reach potential with ${megaCount} Mega influencers (1M above followers).`;

  const microLeader = brands.reduce((leader, brand) => {
    const currentValue = microTier[brand] || 0;
    const leaderValue = microTier[leader] || 0;
    return currentValue > leaderValue ? brand : leader;
  }, brands[0]);

  const microCount = microTier[microLeader];

  return `${megaLeader} achieves best reach potential with ${megaCount} Mega influencers (1M above followers). ${microLeader} leads in scalable Micro tier with ${microCount} influencers (10K-100K), offering growth opportunities.`;
}

/**
 * Generate insight for Interactive Chart (Brand Comparison)
 */
export function generateBrandComparisonInsight(data, selectedMetric) {
  if (!data || data.length === 0) return "";

  const leader = findLeader(data, selectedMetric);
  if (!leader || !leader.brand) return "";

  const runnerUp = findRunnerUp(data, selectedMetric, leader.brand);

  if (!runnerUp) return `${leader.brand} leads in ${selectedMetric}.`;

  const leaderValue = leader[selectedMetric] || 0;
  const runnerUpValue = runnerUp[selectedMetric] || 0;
  const gap = leaderValue - runnerUpValue;
  const percentDiff = calculatePercentageDiff(leaderValue, runnerUpValue);

  const metricLabels = {
    creatorCount: 'creators',
    posts: 'posts',
    views: 'total views',
    engagement: 'engagement'
  };

  const metricLabel = metricLabels[selectedMetric] || selectedMetric;
  const formattedLeaderValue = formatLargeNumber(leaderValue);
  const formattedGap = selectedMetric === 'views' ? formatLargeNumber(gap) : gap;

  return `${leader.brand} leads with ${formattedLeaderValue} ${metricLabel}, ahead of ${runnerUp.brand} by ${formattedGap} (${percentDiff}% higher), demonstrating ${selectedMetric === 'engagement' ? 'superior audience connection' : 'stronger market presence'}.`;
}

/**
 * Generate insight for the Content Themes ranking
 */
export function generateContentThemeInsight(themes) {
  if (!themes || themes.length === 0) return "";

  const leader = findLeader(themes, 'post_count');
  if (!leader || !leader.label) return "";

  const totalPosts = themes.reduce((sum, t) => sum + (t.post_count || 0), 0);
  const share = totalPosts > 0 ? ((leader.post_count / totalPosts) * 100).toFixed(0) : 0;

  const topByEngagement = findLeader(themes, 'avg_engagement_rate');
  const engagementNote =
    topByEngagement && topByEngagement.label && topByEngagement.label !== leader.label
      ? ` ${topByEngagement.label} is produced less often but earns the highest engagement at ${(topByEngagement.avg_engagement_rate || 0).toFixed(1)}%, suggesting room to shift the content mix.`
      : ` It also leads on engagement at ${(leader.avg_engagement_rate || 0).toFixed(1)}%.`;

  return `${leader.label} is the most used content theme with ${leader.post_count} posts (${share}% of analysed content) across ${leader.creators_count || 0} creators.${engagementNote}`;
}

/**
 * Generate insight for the Hooks ranking
 */
export function generateHookInsight(hooks) {
  if (!hooks || hooks.length === 0) return "";

  const leader = findLeader(hooks, 'post_count');
  if (!leader || !leader.label) return "";

  const totalPosts = hooks.reduce((sum, h) => sum + (h.post_count || 0), 0);
  const share = totalPosts > 0 ? ((leader.post_count / totalPosts) * 100).toFixed(0) : 0;

  const topByEngagement = findLeader(hooks, 'avg_engagement_rate');
  const engagementNote =
    topByEngagement && topByEngagement.label && topByEngagement.label !== leader.label
      ? ` The ${topByEngagement.label} opening converts best at ${(topByEngagement.avg_engagement_rate || 0).toFixed(1)}% engagement.`
      : ` It also converts best at ${(leader.avg_engagement_rate || 0).toFixed(1)}% engagement.`;

  return `Creators most often open with a ${leader.label} — ${leader.post_count} posts (${share}% of analysed content).${engagementNote}`;
}
